import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMounted, usePreservedCallback } from '@toss/react';
import { noop } from 'lodash-es';
import { useRouter } from 'next/router';
import { type GetAuthProfileResponse, useGetAuthProfile } from '@/api';
import { isIgnoreError } from '@/api/apiUtil';
import { useDuplicateLoginDetector } from '@/hooks/useDuplicateLoginDetector';
import { useLocale } from '@/hooks/useLocale';
import { useUserLocalStorageState } from '@/lib/auth/hooks/useUserLocalStorageState';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { mapTeacherSchoolInfo } from '@/shared/category/util/SchoolMapper';
import { UserCookieManager } from '@/shared/cookies';
import { GoogleOAuthStorage } from '@/shared/google';
import { type UserInfo, type UserType } from '@/types';
import { combinePathAndQuery } from '@/utils/combinePathAndQuery';
import { getSafeRedirectUrl } from '@/utils/urlValidation';
import { setLogUserInfo } from './setLogUserInfo';
import { clearUserSession, setUserSession } from './userStorage';
const defaultUserContext: UserContextType = {
  user: null,
  setUser: noop,
  clearUser: noop,
  updateUser: noop,
  syncLatestUserInfo: () => Promise.resolve({} as GetAuthProfileResponse),
  persistSwitchedUserToStorage: () => Promise.resolve(undefined),
  logOut: () => Promise.resolve(),
  isAuthenticated: false,
  isInitialized: false,
  isLoggerUserInfoSet: false
};
export const UserContext = createContext<UserContextType>(defaultUserContext);

/** getAuthProfile 응답 + 토큰을 앱 UserInfo 로 매핑한다(syncLatestUserInfo / 계정 전환 영속화 공유). */
function buildUserInfo(latestUserInfo: GetAuthProfileResponse, token: string): UserInfo {
  return {
    isAmbassador: latestUserInfo.user.grade === 'AMBASSADOR',
    quizUserId: latestUserInfo.user.quizUserId,
    token,
    // memberType 은 백엔드 enum 명(대문자: STUDENT/TEACHER/...)으로 내려오는데
    // UserType 은 소문자 union 이다. as 캐스트만 하면 'STUDENT' 가 그대로 들어가
    // user.type === 'student' 비교가 전부 빗나간다(예: 학생 오너에게 관리탭 노출).
    // 선언 타입에 맞춰 소문자로 정규화한다.
    type: (latestUserInfo.user.memberType?.toLowerCase() || 'unknown') as UserType,
    countryCode: latestUserInfo.user.countryCode,
    admin: latestUserInfo.user.isAdmin,
    email: latestUserInfo.user.email ?? '',
    username: latestUserInfo.user.userName,
    expiredAt: latestUserInfo.token.expiredAt,
    registrationStatus: latestUserInfo.user.registrationStatus,
    planType: latestUserInfo.plan.planType,
    planStatus: latestUserInfo.plan.planStatus,
    upcomingPlan: latestUserInfo.plan.upcomingPlan ?? null,
    planDuration: {
      startDate: latestUserInfo.plan.startDate ?? '',
      endDate: latestUserInfo.plan.endDate ?? ''
    },
    workspace: {
      workspaceHashId: latestUserInfo.workspace?.hashId ?? '',
      isPaidOwner: latestUserInfo.workspace?.isPaidOwner ?? false,
      workspaceName: latestUserInfo.workspace?.name ?? ''
    },
    teacherSchoolInfo: mapTeacherSchoolInfo(latestUserInfo.user.teacherSchoolInfo),
    recentQuizsetInfo: latestUserInfo.user.recentQuizsetInfo,
    phoneNumber: latestUserInfo.user.phoneNumber
  };
}
export const SKIP_REDIRECT_PATHS = ['/login',
// 학생코드/QR 로그인 진입 페이지. 온보딩 미완료(registrationStatus=false) 계정이 남은
// 브라우저에서 /code-login 을 열면 코드 검증 전에 /sign-up/type 으로 튕겨 학생 코드 로그인·
// 계정 전환을 못 한다. startsWith('/login') 은 '/code-login' 을 매칭 안 하므로 별도 등록.
'/code-login', '/sign-up', '/duplicate-login', '/trouble', '/terms', '/policy', '/update-browser', '/admin', '/auth/google-callback', '/auth/whalespace-callback'] as const;
export const UserProvider = ({
  children
}: PropsWithChildren) => {
  const router = useRouter();
  const pathName = router.pathname;
  const client = useQueryClient();
  const {
    locale
  } = useLocale();
  const isMounted = useIsMounted();
  const {
    refetch: fetchUserInfo
  } = useGetAuthProfile({
    query: {
      enabled: false,
      throwOnError: error => !isIgnoreError(error),
      meta: {
        ignoreError: true
      }
    }
  });
  const {
    user,
    setUser,
    clearUser
  } = useUserLocalStorageState();
  const [isLoggerUserInfoSet, setIsLoggerUserInfoSet] = useState(false);
  const shouldRedirectToSignup = usePreservedCallback((user: UserInfo | null, pathname: string): boolean => {
    if (!user || !pathname) return false;

    // 이미 해당 페이지에 있으면 리다이렉트하지 않음
    if (SKIP_REDIRECT_PATHS.some(path => pathname?.startsWith(path))) {
      return false;
    }

    // 유저 상세 가입이 안되어있을 경우
    return !user.registrationStatus;
  });

  // sign-up 페이지로 리다이렉트하는 함수
  const redirectToSignupPage = usePreservedCallback(() => {
    if (router.pathname.includes('/sign-up')) {
      // 실수로 무한 이동으로 진행중인경우를 방지하는 코드
      return;
    }
    const signupPath = combinePathAndQuery('/sign-up/type', router.query);
    void router.push(signupPath);
  });
  const handleUser = usePreservedCallback((newUser: UserInfo) => {
    setUserSession(newUser);
    setUser({
      ...newUser
    });
    if (shouldRedirectToSignup(newUser, router.pathname)) {
      redirectToSignupPage();
    }
  });
  const logOut = usePreservedCallback(async ({
    redirectTo
  }: {
    redirectTo?: string;
  } = {}) => {
    if (redirectTo != null) {
      await router.push(getSafeRedirectUrl(redirectTo));
    }
    try {
      await fetch(`${process.env.NEXT_PUBLIC_AUTH_API_URL}/auth/logout?service=quiz`, {
        credentials: 'include'
      });
    } catch {
      // auth-server 호출 실패 무시
    }
    clearUserSession();
    client.clear();
    clearUser();
    GoogleOAuthStorage.resetToken();
  });
  const updateUser = usePreservedCallback((updatedUser: Partial<UserInfo>) => {
    const newUser = {
      ...user,
      ...updatedUser
    };
    if (newUser) {
      setUserSession(newUser);
      setUser(newUser as UserInfo);
    }
  });
  const syncLatestUserInfo = usePreservedCallback(async (token: string) => {
    const {
      data
    } = await fetchUserInfo();
    if (!data) return {} as GetAuthProfileResponse;
    const {
      data: latestUserInfo
    } = data;
    if (latestUserInfo.user.countryCode && latestUserInfo.token.expiredAt) {
      UserCookieManager.setUserCountryCode(latestUserInfo.user.countryCode, latestUserInfo.token.expiredAt);
    }
    const newUser = buildUserInfo(latestUserInfo, token);
    handleUser(newUser);
    return latestUserInfo;
  });

  // 계정 전환 풀로드 직전 전용: user 를 React state(setUser)가 아니라 localStorage('quiz_user')에만
  // B 로 기록한다. setUser 를 쓰면 SubscribeUserPlan 등이 리렌더되며 #185 루프가 나므로, setState 를
  // 거치지 않는다. 풀로드 후 새 페이지가 이 값을 읽어 B 로 깨끗하게 부팅된다(use-local-storage-state
  // 기본 직렬화 == JSON.stringify, 키 'quiz_user' 와 일치).
  const persistSwitchedUserToStorage = usePreservedCallback(async (token: string): Promise<UserInfo | undefined> => {
    const {
      data
    } = await fetchUserInfo();
    if (!data) return undefined;
    const newUser = buildUserInfo(data.data, token);
    setUserSession(newUser);
    window.localStorage.setItem('quiz_user', JSON.stringify(newUser));
    // 풀로드 직전, 호출 측이 newUser 의 registrationStatus/type 으로 도착지를 정하도록 반환한다.
    return newUser;
  });
  const isExpiredUser = useMemo(() => {
    if (!user) return false;
    return user.expiredAt && new Date(user.expiredAt) < new Date();
  }, [user]);
  const isAuthenticated = user ? !isExpiredUser : false;

  // 페이지 이동 시 호출되는 핸들러
  const handleRouteChangeStart = usePreservedCallback((nextUrl: string) => {
    // 만료된 유저 체크
    if (isExpiredUser) {
      void logOut();
      return;
    }
    const localePath = `/${locale}`;
    const pathname = nextUrl.startsWith(localePath) ? nextUrl.split(localePath)[1] : nextUrl;
    // 가입이 완료되지 않은 유저 체크
    if (shouldRedirectToSignup(user, pathname)) {
      redirectToSignupPage();
    }
  });
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [handleRouteChangeStart, router]);
  useEffect(() => {
    // 로그인 "진입" 페이지에서만 이전 세션을 초기화한다.
    // 주의: '/auth/sso-exchange-token', '/auth/sso-create-account' 같은 SSO 콜백/랜딩
    // 페이지는 각 콜백에서 새 로그인 처리 직전에 한 번만 초기화한다. 여기서 path 기준으로
    // 계속 clear 하면 방금 발급한 token/quiz_user 가 지워져, 어드민 프록시(/admin)
    // 로그인 후 다시 로그인으로 튕기는 무한 루프가 발생한다.
    if (pathName === '/login') {
      clearUserSession();
      clearUser();
      GoogleOAuthStorage.resetToken();
    }
  }, [clearUser, pathName]);

  // 초기 로드 시 sign-up 페이지 리다이렉트 체크
  useEffect(() => {
    if (isMounted && user) {
      if (shouldRedirectToSignup(user, router.pathname)) {
        redirectToSignupPage();
      }
    }
  }, [isMounted, user, shouldRedirectToSignup, redirectToSignupPage, router.pathname]);
  useEffect(() => {
    if (isMounted) {
      // isMounted 시점에 user 의 값으로 판단한다(로컬스토리 초기화 타이밍이다)
      if (user) {
        setUserSession(user);
      } else {
        clearUserSession();
      }
      if (!pathName.includes('/admin')) {
        setLogUserInfo(user);
      }
      // 유저 정보가 로거에 설정되었음을 표시 (로그인/비로그인 모두)
      setIsLoggerUserInfoSet(true);
    }
  }, [user, isMounted, pathName]);

  // 로컬스토리지에 로그인 유저로 캐시되어 있는 경우, 앱 시작 시 서버와 동기화
  useEffect(() => {
    const syncUserInfo = async () => {
      await syncLatestUserInfo(ApiTokenStorage.get());
    };
    if (isAuthenticated) {
      void syncUserInfo();
    }
  }, [isAuthenticated, syncLatestUserInfo]);

  // 중복접속 방지 훅 실행
  useDuplicateLoginDetector({
    user: isAuthenticated ? user : null,
    onDetect: logOut
  });
  return <UserContext.Provider value={{
    isAuthenticated: user ? !isExpiredUser : false,
    isInitialized: isMounted,
    user: isExpiredUser ? null : user,
    setUser: handleUser,
    clearUser,
    updateUser,
    syncLatestUserInfo,
    persistSwitchedUserToStorage,
    logOut,
    isLoggerUserInfoSet
  }} data-sentry-element="unknown" data-sentry-component="UserProvider" data-sentry-source-file="UserContext.tsx">
      {children}
    </UserContext.Provider>;
};
type UserContextType = {
  user: UserInfo | null;
  setUser: (user: UserInfo) => void;
  clearUser: () => void;
  updateUser(user: Partial<UserInfo>): void;
  syncLatestUserInfo: (token: string) => Promise<GetAuthProfileResponse>;
  /** 계정 전환 풀로드 직전, localStorage 에 새 user 를 기록하고 그 user 를 반환한다(도착지 결정용). */
  persistSwitchedUserToStorage: (token: string) => Promise<UserInfo | undefined>;
  logOut: (props?: {
    redirectTo?: string;
    callbackUrl?: string;
  }) => Promise<void>;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoggerUserInfoSet: boolean;
};

/**
 * 유저 정보가 로거에 설정되었는지 확인하는 훅
 * 초기 로깅 이벤트에서 유저 정보를 포함하려면 이 값이 true일 때 이벤트를 발생시켜야 함
 */
export const useIsLoggerUserInfoSet = () => {
  const {
    isLoggerUserInfoSet
  } = useContext(UserContext);
  return isLoggerUserInfoSet;
};